import type { InputIntent } from "../domain/types";

export class MobileInputController {
  private lastPointerDispatch: { key: string; at: number } | null = null;
  private readonly preview = document.createElement("div");
  private activePointerId: number | null = null;
  private activeKey: HTMLButtonElement | null = null;

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse" || !event.isPrimary || event.button !== 0)
      return;
    const key = this.resolveKey(event);
    if (!key) return;
    event.preventDefault();
    this.releasePointer();
    this.activePointerId = event.pointerId;
    this.activeKey = key;
    key.classList.add("is-pressed");
    try {
      this.keyboard.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic events and older WebKit versions may not expose capture.
    }
    this.showPreview(key);
    this.lastPointerDispatch = {
      key: key.dataset.key ?? "",
      at: performance.now(),
    };
    this.dispatchKey(key, false);
  };

  private readonly onPointerEnd = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    event.preventDefault();
    this.releasePointer();
  };

  private readonly onLostPointerCapture = (event: PointerEvent): void => {
    if (event.pointerId === this.activePointerId) this.releasePointer();
  };

  private readonly onClick = (event: MouseEvent): void => {
    const target = event.target;
    const key =
      target instanceof Element
        ? target.closest<HTMLButtonElement>("[data-key]")
        : null;
    if (!key || key.disabled || !key.dataset.key) return;
    const pointerType = event instanceof PointerEvent ? event.pointerType : "";
    const previous = this.lastPointerDispatch;
    if (
      previous?.key === key.dataset.key &&
      performance.now() - previous.at < 700 &&
      pointerType !== "mouse"
    ) {
      event.preventDefault();
      return;
    }
    this.dispatchKey(key, true);
  };

  constructor(
    private readonly keyboard: HTMLElement,
    private readonly dispatch: (intent: InputIntent) => void,
  ) {
    this.preview.className = "keyboard-key-preview";
    this.preview.hidden = true;
    this.preview.setAttribute("aria-hidden", "true");
    document.body.append(this.preview);
    keyboard.addEventListener("pointerdown", this.onPointerDown);
    keyboard.addEventListener("pointerup", this.onPointerEnd);
    keyboard.addEventListener("pointercancel", this.onPointerEnd);
    keyboard.addEventListener("lostpointercapture", this.onLostPointerCapture);
    keyboard.addEventListener("click", this.onClick);
  }

  setEnabled(enabled: boolean): void {
    if (!enabled) this.releasePointer();
    this.keyboard
      .querySelectorAll<HTMLButtonElement>("[data-key]")
      .forEach((button) => {
        button.disabled = !enabled;
      });
  }

  dispose(): void {
    this.releasePointer();
    this.keyboard.removeEventListener("pointerdown", this.onPointerDown);
    this.keyboard.removeEventListener("pointerup", this.onPointerEnd);
    this.keyboard.removeEventListener("pointercancel", this.onPointerEnd);
    this.keyboard.removeEventListener(
      "lostpointercapture",
      this.onLostPointerCapture,
    );
    this.keyboard.removeEventListener("click", this.onClick);
    this.preview.remove();
  }

  private resolveKey(event: PointerEvent): HTMLButtonElement | null {
    const target = event.target;
    const direct =
      target instanceof Element
        ? target.closest<HTMLButtonElement>("[data-key]")
        : null;
    if (direct && !direct.disabled) return direct;

    let nearest: HTMLButtonElement | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    this.keyboard
      .querySelectorAll<HTMLButtonElement>("[data-key]:not(:disabled)")
      .forEach((candidate) => {
        const bounds = candidate.getBoundingClientRect();
        const deltaX = event.clientX - (bounds.left + bounds.width / 2);
        const deltaY = event.clientY - (bounds.top + bounds.height / 2);
        const distance = deltaX * deltaX + deltaY * deltaY;
        if (distance < nearestDistance) {
          nearest = candidate;
          nearestDistance = distance;
        }
      });
    return nearest;
  }

  private dispatchKey(key: HTMLButtonElement, flash: boolean): void {
    const value = key.dataset.key;
    if (!value || key.disabled) return;
    if (flash) {
      key.classList.remove("is-pressed");
      void key.offsetWidth;
      key.classList.add("is-pressed");
      window.setTimeout(() => key.classList.remove("is-pressed"), 100);
    }
    this.dispatch({ type: "letter", value });
  }

  private showPreview(key: HTMLButtonElement): void {
    this.preview.textContent = key.dataset.key ?? "";
    this.preview.hidden = false;
    this.preview.classList.remove("keyboard-key-preview--visible");
    const keyBounds = key.getBoundingClientRect();
    const previewBounds = this.preview.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const margin = 8;
    const desiredLeft =
      keyBounds.left + keyBounds.width / 2 - previewBounds.width / 2;
    const desiredTop = keyBounds.top - previewBounds.height + 8;
    const left = Math.max(
      viewportLeft + margin,
      Math.min(
        desiredLeft,
        viewportLeft + viewportWidth - previewBounds.width - margin,
      ),
    );
    const top = Math.max(
      viewportTop + margin,
      Math.min(
        desiredTop,
        viewportTop + viewportHeight - previewBounds.height - margin,
      ),
    );
    this.preview.style.left = `${Math.round(left)}px`;
    this.preview.style.top = `${Math.round(top)}px`;
    void this.preview.offsetWidth;
    this.preview.classList.add("keyboard-key-preview--visible");
  }

  private releasePointer(): void {
    const pointerId = this.activePointerId;
    this.activeKey?.classList.remove("is-pressed");
    this.activeKey = null;
    this.activePointerId = null;
    if (pointerId !== null && this.keyboard.hasPointerCapture(pointerId)) {
      try {
        this.keyboard.releasePointerCapture(pointerId);
      } catch {
        // The browser may already have released capture after pointerup.
      }
    }
    this.preview.hidden = true;
    this.preview.classList.remove("keyboard-key-preview--visible");
  }
}
